'use client';

import { TrophyIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../lib/utils';
import type { RankedAnimal } from '../types';
import { MatchPercentBadge } from './MatchPercentBadge';
import {
  ShareToSocialsPanel,
  type ShareChannelHandlers,
  type ShareOverlayPalette,
} from './ShareToSocialsPanel';

export type MatchShareTopThreeSlideProps = ShareChannelHandlers & {
  /** Pre-sorted top entries (length 1–3). */
  top: RankedAnimal[];
  emailShareDisabled?: boolean;
  /** Dark Figma shell when rendered inside `MatchShareResultsFlow`. */
  palette?: ShareOverlayPalette;
  className?: string;
};

function PetPhoto({
  animal,
  alt,
  className,
}: {
  animal: RankedAnimal['animal'];
  alt: string;
  className?: string;
}) {
  if (animal.CoverPhoto) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- shelter CDN URLs
      <img
        src={animal.CoverPhoto}
        alt={alt}
        className={cn('size-full object-cover', className)}
      />
    );
  }
  return (
    <div
      className={cn(
        'flex size-full items-center justify-center bg-accent text-sm text-muted-foreground',
        className,
      )}
    >
      No photo
    </div>
  );
}

function HeroCard({
  item,
  rank,
  palette = 'default',
}: {
  item: RankedAnimal;
  rank: number;
  palette?: ShareOverlayPalette;
}) {
  const { animal, matchPercent } = item;
  const dark = palette === 'figmaDark';
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border shadow-sm',
        dark ? 'border-white/10 bg-black/25' : 'border-border bg-card',
        /* Cap hero height on narrow viewports so header + share strip stay discoverable */
        'aspect-[4/5] w-full max-h-[min(48vh,22rem)] sm:max-h-none lg:aspect-[3/4]',
      )}
    >
      <PetPhoto animal={animal} alt={animal.Name} />
      <div
        className={cn(
          'pointer-events-none absolute inset-0 bg-linear-to-t via-transparent to-transparent',
          dark ? 'from-black/65' : 'from-foreground/50',
        )}
      />
      <div className='absolute left-3 top-3 flex flex-wrap gap-2'>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm',
            'bg-highlight text-highlight-foreground',
          )}
        >
          <TrophyIcon className='size-3.5' aria-hidden />
          Top match
        </span>
      </div>
      <div className='absolute right-3 top-3'>
        <MatchPercentBadge matchPercent={matchPercent} />
      </div>
      <p className='absolute bottom-3 left-3 font-heading text-xl font-bold text-white sm:text-2xl'>
        {animal.Name}
      </p>
      <span className='sr-only'>Rank {rank}</span>
    </div>
  );
}

function SecondaryCard({
  item,
  rank,
  palette = 'default',
  className,
}: {
  item: RankedAnimal;
  rank: number;
  palette?: ShareOverlayPalette;
  className?: string;
}) {
  const { animal, matchPercent } = item;
  const dark = palette === 'figmaDark';
  return (
    <div
      className={cn(
        'relative flex min-h-0 flex-1 overflow-hidden rounded-2xl border shadow-sm',
        dark ? 'border-white/10 bg-black/25' : 'border-border bg-card',
        className,
      )}
    >
      <div className='relative aspect-square w-full shrink-0 sm:aspect-[5/4] lg:aspect-square'>
        <PetPhoto animal={animal} alt={animal.Name} />
        <div
          className={cn(
            'pointer-events-none absolute inset-0 bg-linear-to-t to-transparent',
            dark ? 'from-black/55' : 'from-foreground/40',
          )}
        />
        <div className='absolute right-2 top-2'>
          <MatchPercentBadge matchPercent={matchPercent} />
        </div>
        <div className='absolute bottom-2 left-2'>
          <span
            className={cn(
              'bg-primary-600 px-2.5 py-1 text-xs font-semibold text-primary-foreground',
              dark ? 'rounded-md' : 'rounded-full',
            )}
          >
            #{rank} {animal.Name}
          </span>
        </div>
      </div>
    </div>
  );
}

function TwoUpCard({
  item,
  rank,
  palette = 'default',
}: {
  item: RankedAnimal;
  rank: number;
  palette?: ShareOverlayPalette;
}) {
  const { animal, matchPercent } = item;
  const dark = palette === 'figmaDark';
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border shadow-sm',
        dark ? 'border-white/10 bg-black/25' : 'border-border bg-card',
      )}
    >
      <div className='relative aspect-square w-full'>
        <PetPhoto animal={animal} alt={animal.Name} />
        <div
          className={cn(
            'pointer-events-none absolute inset-0 bg-linear-to-t to-transparent',
            dark ? 'from-black/55' : 'from-foreground/45',
          )}
        />
        <div className='absolute right-2 top-2'>
          <MatchPercentBadge matchPercent={matchPercent} />
        </div>
        <p className='absolute bottom-2 left-2 font-heading text-base font-bold text-white sm:text-lg'>
          #{rank} {animal.Name}
        </p>
      </div>
    </div>
  );
}

export function MatchShareTopThreeSlide({
  top,
  emailShareDisabled = false,
  palette = 'default',
  className,
  ...shareHandlers
}: MatchShareTopThreeSlideProps) {
  const n = top.length;

  return (
    <div className={cn('flex min-h-0 flex-col gap-6', className)}>
      <div className='min-h-0 min-w-0 flex-1'>
        {n === 1 ? (
          <div className='mx-auto w-full max-w-lg'>
            <HeroCard item={top[0]!} rank={1} palette={palette} />
          </div>
        ) : null}

        {n === 2 ? (
          <div className='grid grid-cols-2 gap-3 sm:gap-4'>
            <TwoUpCard item={top[0]!} rank={1} palette={palette} />
            <TwoUpCard item={top[1]!} rank={2} palette={palette} />
          </div>
        ) : null}

        {n >= 3 ? (
          <div className='flex min-h-0 flex-col gap-4 lg:grid lg:min-h-[22rem] lg:grid-cols-12 lg:grid-rows-2 lg:items-stretch lg:gap-4'>
            <div className='min-h-0 lg:col-span-6 lg:row-span-2'>
              <HeroCard item={top[0]!} rank={1} palette={palette} />
            </div>
            <div
              className={cn(
                'grid min-h-0 grid-cols-2 gap-3',
                'lg:col-span-3 lg:row-span-2 lg:flex lg:min-h-[22rem] lg:flex-col lg:gap-4',
              )}
            >
              <SecondaryCard
                item={top[1]!}
                rank={2}
                palette={palette}
                className='min-h-0 lg:flex-1 lg:basis-0'
              />
              <SecondaryCard
                item={top[2]!}
                rank={3}
                palette={palette}
                className='min-h-0 lg:flex-1 lg:basis-0'
              />
            </div>
            <div className='hidden min-h-0 lg:col-span-3 lg:row-span-2 lg:block'>
              <ShareToSocialsPanel
                layout='column'
                className='h-full min-h-0'
                palette={palette}
                emailDisabled={emailShareDisabled}
                {...shareHandlers}
              />
            </div>
          </div>
        ) : null}
      </div>

      {n >= 3 ? (
        <div className='lg:hidden'>
          <ShareToSocialsPanel
            layout='row'
            palette={palette}
            emailDisabled={emailShareDisabled}
            {...shareHandlers}
          />
        </div>
      ) : (
        <ShareToSocialsPanel
          layout='row'
          palette={palette}
          emailDisabled={emailShareDisabled}
          {...shareHandlers}
        />
      )}
    </div>
  );
}
